import apiV2 from "api-v2";
import type {
  Meeting,
  MeetingAttendee,
  MeetingCriteriaScore,
  OverallScores,
  AwardsResponse,
  CreateMeetingRequest,
  MeetingOverrideRequest,
  MeetingReopenRequest,
  AddAttendeeRequest,
  UpdateAttendeeRequest,
  SendInvitesRequest,
  UpsertConsensusScoresRequest,
  UpsertAwardsRequest,
} from "../types/meeting.type";

const unwrap = <T>(responseData: unknown): T => {
  if (responseData && typeof responseData === "object" && "data" in responseData) {
    return (responseData as Record<string, unknown>).data as T;
  }
  return responseData as T;
};

const meetingBase = (rfxId: number, evalId: number): string =>
  `/rfx/${rfxId}/evaluation/${evalId}/meeting`;

const awardsBase = (rfxId: number, evalId: number): string =>
  `/rfx/${rfxId}/evaluation/${evalId}/awards`;

// --- Meeting CRUD ---

export const getMeeting = async (
  rfxId: number,
  evalId: number
): Promise<Meeting> => {
  const response = await apiV2.get(meetingBase(rfxId, evalId));
  return unwrap<Meeting>(response.data);
};

export const createMeeting = async (
  rfxId: number,
  evalId: number,
  payload: CreateMeetingRequest
): Promise<Meeting> => {
  const response = await apiV2.post(meetingBase(rfxId, evalId), payload);
  return unwrap<Meeting>(response.data);
};

export const updateMeeting = async (
  rfxId: number,
  evalId: number,
  payload: CreateMeetingRequest
): Promise<Meeting> => {
  const response = await apiV2.patch(meetingBase(rfxId, evalId), payload);
  return unwrap<Meeting>(response.data);
};

export const overrideMeeting = async (
  rfxId: number,
  evalId: number,
  payload: MeetingOverrideRequest
): Promise<Meeting> => {
  const response = await apiV2.post(`${meetingBase(rfxId, evalId)}/override`, payload);
  return unwrap<Meeting>(response.data);
};

export const startMeeting = async (
  rfxId: number,
  evalId: number
): Promise<Meeting> => {
  const response = await apiV2.post(`${meetingBase(rfxId, evalId)}/start`);
  return unwrap<Meeting>(response.data);
};

export const completeMeeting = async (
  rfxId: number,
  evalId: number
): Promise<Meeting> => {
  const response = await apiV2.post(`${meetingBase(rfxId, evalId)}/complete`);
  return unwrap<Meeting>(response.data);
};

export const reopenMeeting = async (
  rfxId: number,
  evalId: number,
  payload: MeetingReopenRequest
): Promise<Meeting> => {
  const response = await apiV2.post(`${meetingBase(rfxId, evalId)}/reopen`, payload);
  return unwrap<Meeting>(response.data);
};

// --- Attendees ---

export const getMeetingAttendees = async (
  rfxId: number,
  evalId: number
): Promise<MeetingAttendee[]> => {
  const response = await apiV2.get(`${meetingBase(rfxId, evalId)}/attendees`);
  return unwrap<MeetingAttendee[]>(response.data) ?? [];
};

export const addMeetingAttendee = async (
  rfxId: number,
  evalId: number,
  payload: AddAttendeeRequest
): Promise<MeetingAttendee> => {
  const response = await apiV2.post(`${meetingBase(rfxId, evalId)}/attendees`, payload);
  return unwrap<MeetingAttendee>(response.data);
};

export const updateMeetingAttendee = async (
  rfxId: number,
  evalId: number,
  attendeeId: number,
  payload: UpdateAttendeeRequest
): Promise<MeetingAttendee> => {
  const response = await apiV2.patch(
    `${meetingBase(rfxId, evalId)}/attendees/${attendeeId}`,
    payload
  );
  return unwrap<MeetingAttendee>(response.data);
};

export const sendMeetingInvites = async (
  rfxId: number,
  evalId: number,
  payload: SendInvitesRequest
): Promise<void> => {
  await apiV2.post(`${meetingBase(rfxId, evalId)}/invite`, payload);
};

// --- Scores ---

export const getMeetingScoresProgress = async (
  rfxId: number,
  evalId: number
): Promise<{ total_criteria: number; completed_criteria: number; is_complete: boolean }> => {
  const response = await apiV2.get(`${meetingBase(rfxId, evalId)}/scores/progress`);
  return unwrap<{ total_criteria: number; completed_criteria: number; is_complete: boolean }>(response.data);
};

export const getMeetingOverallScores = async (
  rfxId: number,
  evalId: number
): Promise<OverallScores> => {
  const response = await apiV2.get(`${meetingBase(rfxId, evalId)}/scores/overall`);
  return unwrap<OverallScores>(response.data);
};

export const getMeetingCriteriaScores = async (
  rfxId: number,
  evalId: number,
  criteriaId: number
): Promise<MeetingCriteriaScore> => {
  const response = await apiV2.get(`${meetingBase(rfxId, evalId)}/scores/${criteriaId}`);
  return unwrap<MeetingCriteriaScore>(response.data);
};

export const upsertConsensusScores = async (
  rfxId: number,
  evalId: number,
  criteriaId: number,
  payload: UpsertConsensusScoresRequest
): Promise<MeetingCriteriaScore> => {
  const response = await apiV2.put(
    `${meetingBase(rfxId, evalId)}/scores/${criteriaId}`,
    payload
  );
  return unwrap<MeetingCriteriaScore>(response.data);
};

// --- Awards ---

export const getAwards = async (
  rfxId: number,
  evalId: number
): Promise<AwardsResponse> => {
  const response = await apiV2.get(awardsBase(rfxId, evalId));
  return unwrap<AwardsResponse>(response.data);
};

export const upsertAwards = async (
  rfxId: number,
  evalId: number,
  payload: UpsertAwardsRequest
): Promise<AwardsResponse> => {
  const response = await apiV2.put(awardsBase(rfxId, evalId), payload);
  return unwrap<AwardsResponse>(response.data);
};
